/**
 * AuthContext Adapter
 * Wraps AuthContext to provide adapter interface for dependency injection
 * 
 * NOTE: This adapter must be created inside a React component/hook where useAuth can be called.
 * The adapter methods will call useAuth() each time they're invoked to get the latest context.
 */

import type { AuthContextAdapter as IAuthContextAdapter } from '../types/adapters.types';
import type { User } from '../../../context/AuthContext';
import type { UserProfile } from '../../../validation/userProfileSchemas';
import type { AuthContextType } from '../../../context/AuthContext';

/**
 * Create AuthContext adapter from AuthContext value
 * 
 * @param authContext - AuthContext value from useAuth() hook
 * @returns AuthContextAdapter implementation
 */
export function createAuthContextAdapter(
  authContext: AuthContextType
): IAuthContextAdapter {
  return {
    login: (
      accessToken: string,
      refreshToken: string | null,
      user: User,
      expiresIn?: number,
      userProfile?: UserProfile | null
    ) => {
      authContext.login(accessToken, refreshToken, user, expiresIn, userProfile);
    },
    logout: async () => {
      await authContext.logout();
    },
    clearAuthStateSilently: async () => {
      await authContext.clearAuthStateSilently();
    },
    setLogoutInProgress: (value: boolean) => {
      authContext.setLogoutInProgress(value);
    },
    get isAuthenticated() {
      return authContext.isAuthenticated;
    },
    get authState() {
      return authContext.authState;
    },
    get user() {
      return authContext.user;
    },
  };
}
